import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { formatDateTime } from '../utils/date';
import { API_BASE } from '../config';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface ReportedScam {
  id: number;
  url: string;
  firstSeenAt: string;
  scamType?: string;
  description?: string;
}

export function ScamDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [scam, setScam] = useState<ReportedScam | null>(null);
  const [loading, setLoading] = useState(true);

  // Add noindex for scam detail pages
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    metaRobots.id = 'scam-detail-noindex';
    document.head.appendChild(metaRobots);
    
    return () => {
      const existing = document.getElementById('scam-detail-noindex');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const loadScam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/scams`);
      const data: ReportedScam[] = await res.json();
      const found = data.find((s) => s.id === Number(id));
      setScam(found || null);
    } catch (error) {
      console.error('Failed to load scam:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadScam();
  }, [loadScam]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="content-wrapper--narrow content-center text-gray-500">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (!scam) {
    return (
      <div className="page-container">
        <div className="content-wrapper--narrow content-center">
          <p className="text-gray-500 mb-4">{t('scams.scamNotFound')}</p>
          <Link to="/scams" className="btn-text">
            {t('scams.backToList')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="content-wrapper--narrow">
        <div className="mb-4 flex items-center justify-between">
          <Link to="/scams" className="btn-text inline-block">
            ← {t('scams.backToList')}
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="card-compact">
          <div className="mb-4">
            <div className="logo-detail logo-placeholder rounded" aria-hidden="true">
              <span className="sr-only">{t('alt.logoPlaceholder')}</span>
            </div>
          </div>
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded">
              {scam.scamType || t('scams.unknownType')}
            </span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4 break-all font-mono">
            {scam.url}
          </h1>

          {scam.description && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-900 mb-2">
                {t('scams.detailDescription')}
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{scam.description}</p>
            </div>
          )}

          <div className="text-sm text-gray-600">
            {t('scams.firstSeenAt')}: {formatDateTime(scam.firstSeenAt)}
          </div>

          <div className="alert-warning mt-6">
            <p className="text-sm text-gray-700">{t('scams.warning')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
